import React, { useState, useEffect, useContext } from 'react'
import { LassieContext } from "../../../context/LassieProvider";
import { organizationUserAPI } from '../../../api';
import { updateUserRoleInUsers, updateUserRoleInWindfarms } from './organizationHelpers';
import WplButton from '../../../components/wpl-button/WplButton';
import AlertPopup from '../../../components/alert-popup/AlertPopup';
import CenteredPopup from '../../../components/centered-popup/CenteredPopup';
import WplSwitch from '../../../components/wpl-switch/wplSwitch';
import WplInput from '../../../components/wpl-input/WplInput';
import TurbineAnimation from '../../../components/turbine-animation/TurbineAnimation';
import OrganizationViews from '../../../components/organization-view/OrganizationViews';
import InspectionTemplatesManager from '../../../components/inspection-templates-manager/InspectionTemplatesManager';
import './lightning-organization-settings.css'

export default function LightningOrganizationSettings() {
    const { fetchOrganizations, fetchOrganizationUsers, fetchOrganizationWindfarms, organization,
        organizationLoading, setOrganizationLoading, users, setUsers, setOrganizationWindfarms, organizationPopup, setOrganizationPopup, activeNotificationWindfarmID, selectedUserID, showAddUserPopup, getInspectionTemplates,
        setShowAddUserPopup, newDueDate, setNewDueDate, setInspectionTemplates, editMode } = useContext(LassieContext);
    const [isUserView, setIsUserView] = useState(true);
    const [userName, setUserName] = useState('')
    const [userEmail, setUserEmail] = useState('')
    const [isAdmin, setIsAdmin] = useState(false)
    const [selectedInvitationWindfarms, setSelectedInvitationWindfarms] = useState([])

    useEffect(() => {
        fetchOrganizations()
    }, [])

    useEffect(() => {
        if (!organization || !organization.id) return;
        getInspectionTemplates(organization.id);
        if (isUserView) {
            fetchOrganizationUsers(organization);
        } else {
            fetchOrganizationWindfarms(organization);
        }
    }, [organization, isUserView]);

    useEffect(() => {
        setUserEmail('')
        setUserEmail('')
        setSelectedInvitationWindfarms([])
    }, [showAddUserPopup])

    const sendOrganizationInvitation = async (user_name, email, windfarm_ids, is_admin, organization_id) => {
        setOrganizationLoading(prev => ({ ...prev, invitation: true }));
        setShowAddUserPopup(false)
        try {
            const response = await organizationUserAPI.createUser(user_name, email, windfarm_ids, is_admin, organization_id)
            setUsers(response)
            setOrganizationPopup({ show: true, status: 'success', title: 'Invitation sent', buttonText: 'Proceed', buttonStatus: 'success', text: `An invitation email was sent to ${email}. The user will appear here once they accept the Terms of Service and set their password.` })
        } catch (error) {
            const rawErrorData = error.response?.data;
            const errorText = typeof rawErrorData === 'string' ? rawErrorData.trim() : null;
            const displayMessage = errorText || error.message

            setOrganizationPopup({ show: true, status: 'warning', title: 'Something went wrong', buttonText: 'Close', buttonStatus: 'warning', text: displayMessage || `There's was an issue creating the invitation` })
            console.error(
                `Error sending organization invitation: ${error}`);
        } finally {
            setOrganizationLoading(prev => ({ ...prev, invitation: false }));
        }
    }

    const handleUserAccessRole = async (target_user_id, windfarm_id, updatedRole, isUserView) => {
        setOrganizationLoading(prev => ({
            ...prev,
            updatedRole: { ...prev.updatedRole, [isUserView ? windfarm_id : target_user_id]: true }
        }));
        setOrganizationPopup(prev => ({ ...prev, show: false }));

        try {
            const response =
                updatedRole === 'no access'
                    ? await organizationUserAPI.removeUserRole(target_user_id, windfarm_id)
                    : await organizationUserAPI.updateUserRole(target_user_id, windfarm_id, updatedRole);

            if (response === 'OK') {
                if (isUserView) {
                    setUsers(prev => updateUserRoleInUsers(prev, target_user_id, windfarm_id, updatedRole));
                } else {
                    setOrganizationWindfarms(prev =>
                        updateUserRoleInWindfarms(prev, target_user_id, windfarm_id, updatedRole)
                    );
                }
            }
        } catch (error) {
            console.error(`Error updating user access role: ${error}`);
        }

        setOrganizationLoading(prev => ({ ...prev, updatedRole: {} }));
    };

    const closePopupAndReset = () => {
        setOrganizationPopup((prev) => ({ ...prev, show: false }))
    }

    const convertToHours = (newDueDate) => {
        if (newDueDate.unit === 'Days') {
            return newDueDate.value * 24;
        }
        return newDueDate.value;
    };

    const popupActionHandlers = {
        'role-update': () =>
            handleUserAccessRole(selectedUserID, activeNotificationWindfarmID, organizationPopup.updated_role, isUserView),
        'update-due-date': () => {
            setInspectionTemplates(prev =>
                prev.map(template =>
                    template.id === editMode.id
                        ? { ...template, due_in_hours: convertToHours(newDueDate) }
                        : template
                )
            );
            closePopupAndReset();
        }
    };

    const getPopupAction = (actionKeyOrFn) => {
        if (typeof actionKeyOrFn === 'string') {
            return popupActionHandlers[actionKeyOrFn];
        }
        return actionKeyOrFn;
    };

    return (
        <div className='organization-container'>
            {organizationLoading.organization ||
                organizationLoading.users ||
                organizationLoading.windfarms ||
                organizationLoading.invitation ||
                organizationLoading.templates ||
                organizationLoading.dueDate ? (
                <TurbineAnimation />
            ) : organization && organization.id ? (
                <>
                    <h4>Manage organization</h4>
                    {organization && organization.name && <div className='organization-name'>{organization.name}</div>}

                    <div className='view-selector-container'>
                        <WplButton
                            className={`view-option ${isUserView ? "selected" : ""}`}
                            value="User view"
                            onClick={() => setIsUserView(true)}
                        />
                        <WplButton
                            className={`view-option ${!isUserView ? "selected" : ""}`}
                            value="Windfarm view"
                            onClick={() => setIsUserView(false)}
                        />
                    </div>

                    <OrganizationViews isUserView={isUserView} />
                    <div className='organization-spacer'></div>
                    <InspectionTemplatesManager />

                    <CenteredPopup showPopup={showAddUserPopup} closePopup={() => setShowAddUserPopup(false)} className='add-user-popup'>
                        <div className='centered-content'>
                            <h4>Add user to {organization.name}</h4>
                        </div>
                        <div className='organization-invitation-container'>
                            <WplInput title="First name" value={userName} onChanged={setUserName} />
                            <WplInput title="Email" value={userEmail} onChanged={setUserEmail} />
                            <WplInput
                                title="Windfarms"
                                options={users.length ? users[0].windfarms.map((wf) => wf.name) : []}
                                onlySelectableOptions={true}
                                onChanged={(selectedNames) => {
                                    const selectedIds = users[0].windfarms
                                        .filter((wf) => selectedNames.includes(wf.name))
                                        .map((wf) => wf.id);
                                    setSelectedInvitationWindfarms(selectedIds);
                                }}
                                value={
                                    users.length > 0
                                        ? users[0].windfarms
                                            .filter((wf) => selectedInvitationWindfarms.includes(wf.id))
                                            .map((wf) => wf.name)
                                        : []
                                }
                                multiSelect={true}
                            />
                            <WplSwitch
                                title={"Adminstrator"}
                                value={isAdmin}
                                onChanged={() => setIsAdmin(!isAdmin)}
                                className="admin-switch"
                                labelClassname={"admin-switch"}
                            />
                            <div className='centered-content'>
                                <WplButton value="Send invitation"
                                    onClick={() => sendOrganizationInvitation(userName, userEmail, selectedInvitationWindfarms, isAdmin, organization.id)} className='invitation-btn'
                                    disabled={!userName || !userEmail || !selectedInvitationWindfarms.length}
                                />
                            </div>

                        </div>
                    </CenteredPopup>

                    {(organizationLoading.organization || organizationLoading.users || organizationLoading.windfarms || organizationLoading.invitation || organizationLoading.templates || organizationLoading.dueDate) && <TurbineAnimation />}

                    <AlertPopup status={organizationPopup.status}
                        title={organizationPopup.title}
                        showPopup={organizationPopup.show}
                        closePopup={() => closePopupAndReset()}
                        buttonText={organizationPopup.buttonText}
                        buttonStatus={organizationPopup.buttonStatus}
                        action={organizationPopup.action === 'role-update' ? () => handleUserAccessRole(selectedUserID, activeNotificationWindfarmID, organizationPopup.updated_role, isUserView) : organizationPopup.action}
                        text={organizationPopup.text}
                        buttonText2={organizationPopup.buttonText2}
                        buttonStatus2={organizationPopup.buttonStatus2}
                        action2={getPopupAction(organizationPopup.action2) || (() => { })}
                    >

                        {organizationPopup.is_due_date &&
                            <div className='due-date-popup'>

                                <WplInput
                                    title="Due in"
                                    type="number"
                                    className='due-date-input'
                                    min={0}
                                    max={100}
                                    value={newDueDate.value}
                                    onChanged={(value) => setNewDueDate({ ...newDueDate, value: value })}
                                />
                                <WplInput
                                    title="Time unit"
                                    options={['Days', "Hours"]}
                                    className='due-date-input'
                                    value={newDueDate.unit}
                                    onChanged={(value) => setNewDueDate({ ...newDueDate, unit: value })}

                                />
                            </div>
                        }
                        {organizationPopup.text && <p>{organizationPopup.text}</p>}
                        {organizationPopup.link && <p >{organizationPopup.link}</p>}
                        {organizationPopup.updated_role && (
                            <WplInput
                                title="Roles"
                                options={['editor', 'viewer', 'no access'].map(role =>
                                    role.replace(/\b\w/g, char => char.toUpperCase())
                                )}
                                value={organizationPopup.updated_role.replace(/\b\w/g, char => char.toUpperCase())}
                                onChanged={(value) =>
                                    setOrganizationPopup(prev => ({ ...prev, updated_role: value.toLowerCase() }))
                                }
                            />
                        )}

                    </AlertPopup>
                </>
            ) : (
                <div>
                    <h4>Manage organization</h4>
                    <div className='disclaimer-container'>
                        <p>Organization Access Required</p>
                        <p>You don’t currently belong to any organization.</p>
                    </div>
                </div>
            )}
        </div>
    )
}